import { ChannelId } from '../channel/channel.type';

export enum RestaurantOptionName {
    INCLUDE_DEPOSITS_IN_TURNOVER = 'INCLUDE_DEPOSITS_IN_TURNOVER',
}

export type RestaurantOption = {
    optionId: number;
    nameValue: RestaurantOptionName;
    isActivated: boolean;
    brandId: number | null;
    restaurantId: number | null;

    created_at: Date;
    updated_at: Date | null;
    deleted_at: Date | null;
};

export type RestaurantOptions = {
    [key in RestaurantOptionName]?: RestaurantOption;
};

export type RestaurantOptionsPayload = {
    [key in RestaurantOptionName]?: Partial<RestaurantOption>;
};

export type UpdateRestaurantOptionsPayload = {
    options: RestaurantOptionsPayload;
    restaurantId: string;
};

export type RestaurantAddress = {
    streetNumber?: string;
    route?: string;
    locality?: string;
    postalCode?: string;
    country?: string;
    lat?: number;
    lng?: number;
    providerId?: string;
    formattedAddress?: string;
};

export type RestaurantPayload = {
    restaurantId: number;
    address: RestaurantAddress | null;
    addressId: number;
    brandId: number;
    contactEmail: string;
    contactName: string | null;
    contactPhone: number;
    currency: string;
    delayTakeAway: number | null;
    externalId: number | null;
    legalId: number | null;
    locale: string;
    name: string;
    phoneIndicator: number | null;
    options?: RestaurantOptions;
    imageUrl?: string;
    restaurantTags?: { value: string }[];
    brandRestaurantFamilies?: { brandRestaurantFamilyId: number; name: string }[];
    ewalletMinimumForScanTray?: number;
    created_at?: string;
};

export type Restaurant = RestaurantPayload & {
    created_at: string;
};

export type GetRestaurantListV2Payload = {
    offset?: number;
    limit?: number;
    search?: string;
    filter?: Record<string, string>;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
};

export type GetRestaurantListV2Response = {
    restaurants: RestaurantSearch[];
    meta: {
        total: number;
    };
};

type RestaurantSearch = {
    restaurantId: string;
    name: string;
    createdAt: Date;
    brandId: string;
    brandName: string;
};

export type GetRestaurantDetailsPayload = {
    restaurantId: string;
};

export type GetRestaurantShiftsPayload = {
    restaurantId: string;
};

export type GetRestaurantShiftsResponse = RestaurantShift[];

export type RestaurantShift = {
    shiftId: string;
    restaurantId: number;

    name: string;
    daysOfWeek: string;
    start: number;
    end: number;
    endsTomorrow: boolean;
    color: string;
    isEnabled: boolean;
    isDefault: boolean;

    createdAt: Date;
    updatedAt: Date;
};

export type ModuleOrderingFlowAttributes = {
    moduleOrderingFlowId: number;
    brandId: number | null;
    restaurantId: number | null;
    isLoginEnabled: boolean;
    mainRestaurantId: number | null;
    mainUserId: number | null;

    is_enabled: boolean;
    created_at: Date;
    updated_at: Date;
};

export type UpdateRestaurantPayload = Partial<
    Omit<RestaurantPayload, 'brandRestaurantFamilies' | 'contactPhone' | 'externalId' | 'restaurantId' | 'address'>
> & {
    brandId: number;
    restaurantId: string | number;
    contactPhone?: string | number;
    externalId?: string | number | null;
    address?: { lng: number; lat: number; providerId?: string; name?: string };
    restaurantTags?: { value: string }[];
    brandRestaurantFamilies?: number[];
    restaurantUuid?: string;
    salesSystem?: string | null;
    lastShiftClosureHour?: string | null;
    operationZone?: {
        operationZoneId: number;
        code: string;
        name: string;
        countryCode: string;
        isPrimary: boolean;
        fiscalityType: string;
    };
};

export type LegalAddress = {
    name?: string | null;
    route?: string | null;
    streetNumber?: string | null;
    postalCode?: string | null;
    locality?: string | null;
    country?: string | null;
    administrativeAreaLevel1?: string | null;
    administrativeAreaLevel2?: string | null;
    provider?: string | null;
    providerId?: string | null;
    lat?: number | null;
    lng?: number | null;
};

export type LegalInfo = {
    legalId?: number;
    socialReason?: string | null;
    organizationType?: string | null;
    capital?: number | null;
    commerceRegistryCity?: string | null;
    siren?: string | null;
    taxNumber?: string | null;
    industryClassificationCode?: string | null;
    publicationDirectorName?: string | null;
    publicationDirectorFunction?: string | null;
    webmasterName?: string | null;
    webmasterEmail?: string | null;
    address?: LegalAddress | null;
    // Write-only: sent on PUT when the user picks a new legal address via Google Places.
    googlePlaceId?: string;
};

export type DeleteRestaurantPayload = {
    restaurantId: string;
};

export type RestaurantModule = {
    [key in ChannelId]: {
        ordering: {
            flow: ModuleOrderingFlowAttributes;
        };
    };
};

export enum WeekDay {
    MON = 'MON',
    TUE = 'TUE',
    WED = 'WED',
    THU = 'THU',
    FRI = 'FRI',
    SAT = 'SAT',
    SUN = 'SUN',
}

export type OrderingQuota = {
    consumptionMode: string;
    orderingQuota: number;
};

export type PickupScheduleSlot = {
    day: WeekDay;
    start: number;
    end: number;
    orderingQuotas?: OrderingQuota[];
};

export type GetPickupSchedulesResponse = {
    modelId: string;
    pickupSchedules: PickupScheduleSlot[];
};

export type SavePickupSchedulesPayload = {
    restaurantId: string;
    pickupSchedules: PickupScheduleSlot[];
};
